// ScheduleSendDialog — modal-ish picker invoked from EmailComposer's
// "Schedule send" action. Pure-UI (no network calls); the host
// component owns the api.sendScheduledEmail call. Renders inline
// inside the composer drawer rather than as a global modal so it
// doesn't fight the drawer's focus trap.
//
// Presets are computed in the user's local timezone — "Tomorrow
// morning" means 8 AM in their browser's locale, not 8 AM UTC. The
// picker emits an ISO-UTC string so the server-side validator can
// compare it cleanly to Date.now().

import { useEffect, useMemo, useState } from "react";

import { Button } from "../../ui";

export interface ScheduleSendDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (sendAtIso: string) => void;
  // The composer's "sending" flag — disable the confirm button while
  // the parent is mid-send so a double-click doesn't fire two
  // requests.
  busy?: boolean;
}

interface Preset {
  id: string;
  label: string;
  sublabel: string;
  date: Date;
}

// Build the four standard presets. Echoes Gmail's "Schedule send"
// menu — two near-future options (later today / tomorrow morning),
// one weekend option (this weekend if it's still upcoming, otherwise
// hide), and one start-of-week option (Monday morning).
//
// Defaults that match advisor habits:
//   * "Later today"      → today, 4:30 PM local (end-of-business)
//   * "Tomorrow morning" → tomorrow, 8 AM local
//   * "This weekend"     → Saturday, 9 AM local (only if today is
//                          Mon–Fri AND Saturday is at least 24h away)
//   * "Monday morning"   → next Monday, 8 AM local
function buildPresets(now: Date): Preset[] {
  const presets: Preset[] = [];

  // "Later today" — only if at least 90 minutes from now AND before
  // 9 PM. After that, "later today" doesn't make practical sense.
  const laterToday = new Date(now);
  laterToday.setHours(16, 30, 0, 0);
  if (laterToday.getTime() > now.getTime() + 90 * 60 * 1000) {
    presets.push({
      id: "later_today",
      label: "Later today",
      sublabel: formatLabel(laterToday),
      date: laterToday,
    });
  }

  // "Tomorrow morning" — tomorrow at 8 AM
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(8, 0, 0, 0);
  presets.push({
    id: "tomorrow_morning",
    label: "Tomorrow morning",
    sublabel: formatLabel(tomorrow),
    date: tomorrow,
  });

  // "This weekend" — upcoming Saturday at 9 AM, only if it's at least
  // 24h away. If today IS Saturday or Sunday, skip this preset.
  const dayOfWeek = now.getDay(); // 0=Sun, 6=Sat
  if (dayOfWeek >= 1 && dayOfWeek <= 5) {
    const saturday = new Date(now);
    const daysUntilSaturday = 6 - dayOfWeek;
    saturday.setDate(saturday.getDate() + daysUntilSaturday);
    saturday.setHours(9, 0, 0, 0);
    if (saturday.getTime() > now.getTime() + 24 * 60 * 60 * 1000) {
      presets.push({
        id: "this_weekend",
        label: "This weekend",
        sublabel: formatLabel(saturday),
        date: saturday,
      });
    }
  }

  // "Monday morning" — next Monday at 8 AM
  const monday = new Date(now);
  // Days until next Monday: if today is Monday → 7; else (8 - dayOfWeek) % 7
  const daysUntilMonday = dayOfWeek === 1 ? 7 : (8 - dayOfWeek) % 7;
  monday.setDate(monday.getDate() + daysUntilMonday);
  monday.setHours(8, 0, 0, 0);
  presets.push({
    id: "monday_morning",
    label: "Monday morning",
    sublabel: formatLabel(monday),
    date: monday,
  });

  return presets;
}

function formatLabel(d: Date): string {
  return d.toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

// `<input type="datetime-local">` wants `YYYY-MM-DDTHH:mm` in local
// time (no timezone suffix). Convert a Date to that format using
// local components.
function toDatetimeLocalValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

// Convert a `datetime-local` value (interpreted as the user's local
// timezone) back to a Date.
function fromDatetimeLocalValue(value: string): Date | null {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function ScheduleSendDialog({
  open,
  onClose,
  onConfirm,
  busy,
}: ScheduleSendDialogProps) {
  // Recompute presets each render so a long-lived composer doesn't
  // show stale "Tomorrow morning" labels after midnight.
  const presets = useMemo(() => buildPresets(new Date()), [open]);

  const [selectedPresetId, setSelectedPresetId] = useState<string | null>(null);
  const [customValue, setCustomValue] = useState("");
  const [error, setError] = useState<string | null>(null);

  // When the dialog opens, default to the first preset.
  useEffect(() => {
    if (open) {
      setSelectedPresetId(presets[0]?.id ?? null);
      setCustomValue("");
      setError(null);
    }
  }, [open, presets]);

  if (!open) return null;

  const handleConfirm = () => {
    setError(null);

    // Custom path takes precedence when the user has touched the
    // datetime-local input — otherwise we use the selected preset.
    let chosen: Date | null = null;
    if (customValue) {
      chosen = fromDatetimeLocalValue(customValue);
      if (!chosen) {
        setError("Choose a valid date and time.");
        return;
      }
    } else {
      const preset = presets.find((p) => p.id === selectedPresetId);
      if (!preset) {
        setError("Pick a time.");
        return;
      }
      chosen = preset.date;
    }

    // Client-side validation matching the server: must be at least 60
    // seconds in the future. Rejecting at the client saves a round
    // trip and gives a friendlier message.
    if (chosen.getTime() < Date.now() + 60_000) {
      setError("Pick a time at least a minute in the future.");
      return;
    }
    const ninetyDays = 90 * 24 * 60 * 60 * 1000;
    if (chosen.getTime() > Date.now() + ninetyDays) {
      setError("Schedule send doesn't go further out than 90 days.");
      return;
    }

    onConfirm(chosen.toISOString());
  };

  return (
    <div className="schedule-send-overlay" role="dialog" aria-modal="true">
      <div className="schedule-send-card">
        <header className="schedule-send-header">
          <h3>Schedule send</h3>
          <button
            type="button"
            className="schedule-send-close"
            onClick={onClose}
            aria-label="Close"
            disabled={busy}
          >
            ×
          </button>
        </header>
        <div className="schedule-send-body">
          <ul className="schedule-send-presets">
            {presets.map((p) => (
              <li key={p.id}>
                <label className="schedule-send-preset-row">
                  <input
                    type="radio"
                    name="schedule-send-preset"
                    value={p.id}
                    checked={selectedPresetId === p.id && !customValue}
                    onChange={() => {
                      setSelectedPresetId(p.id);
                      setCustomValue("");
                    }}
                    disabled={busy}
                  />
                  <span className="schedule-send-preset-label">{p.label}</span>
                  <span className="schedule-send-preset-sublabel">
                    {p.sublabel}
                  </span>
                </label>
              </li>
            ))}
          </ul>
          <div className="schedule-send-custom">
            <label htmlFor="schedule-send-custom-input">
              Or pick a custom time
            </label>
            <input
              id="schedule-send-custom-input"
              type="datetime-local"
              value={customValue}
              min={toDatetimeLocalValue(new Date(Date.now() + 60_000))}
              onChange={(e) => {
                setCustomValue(e.target.value);
                if (e.target.value) setSelectedPresetId(null);
              }}
              disabled={busy}
            />
          </div>
          {error ? (
            <p className="schedule-send-error" role="alert">
              {error}
            </p>
          ) : null}
        </div>
        <footer className="schedule-send-footer">
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={busy}
          >
            {busy ? "Scheduling…" : "Schedule send"}
          </Button>
        </footer>
      </div>
    </div>
  );
}
